import { Link, createFileRoute } from "@tanstack/react-router";
import { MarketingPage, Section, Screenshot } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";
import briefingAsset from "@/assets/demo-mission-control.webp.asset.json";
import journeyAsset from "@/assets/demo-journey.webp.asset.json";
import opportunitiesAsset from "@/assets/demo-jobs.webp.asset.json";
import progressAsset from "@/assets/demo-progress.webp.asset.json";
import howItWorksDiagram from "@/assets/how-monroya-works.png.asset.json";

const PATH = "/how-it-works";
const TITLE = "How Monroya Works: From AI Answers to Verified Wins";
const DESCRIPTION =
  "Enter your website, see who ChatGPT, Claude, Gemini, Perplexity and Google's AI recommend instead of you, get ranked moves with drafts ready, and get proof when they work.";

export const FAQS: FAQ[] = [
  {
    q: "How long does setup take?",
    a: "About a minute. Enter your website, and we confirm your business name and, for local businesses, your town. No card required.",
  },
  {
    q: "Which AI assistants do you check?",
    a: "Paid plans check all 6: ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode. The free plan checks Gemini and Perplexity.",
  },
  {
    q: "How do you know a win is real?",
    a: "A win counts only when the same question on the same assistant names you consistently across repeated checks, not a single lucky answer.",
  },
  {
    q: "Do I need technical skills?",
    a: "No. The drafts and steps are written for you. With the WordPress integration, send a draft directly to your site, review it, edit if you want, and publish. Nothing publishes automatically.",
  },
  {
    q: "How soon will I see changes?",
    a: "Your first results arrive in minutes. You get new moves every week, and wins show up as AI answers change after you publish.",
  },
];

export const Route = createFileRoute("/how-it-works")({
  head: () => {
    const head = marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "How it works", path: PATH },
      ],
      faqs: FAQS,
    });
    return { ...head, meta: [...head.meta, { name: "twitter:card", content: "summary_large_image" }] };
  },
  component: HowItWorksPage,
});

function HowItWorksPage() {
  return (
    <MarketingPage
      stamp="How it works"
      title="How Monroya works."
      dek="Five steps from &ldquo;Does AI recommend us?&rdquo; to proof that it does."
      updated="October 2026"
      faqs={FAQS}
    >
      <div className="flex flex-wrap gap-3">
        <Link
          to="/onboarding"
          className="inline-flex items-center justify-center bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
        >
          Start free
        </Link>
        <Link
          to="/demo"
          className="inline-flex items-center justify-center border border-border bg-paper px-5 py-2.5 text-sm font-medium text-ink"
        >
          See the live demo
        </Link>
      </div>

      <Section heading="1. Enter your website.">
        <p>
          Takes about a minute, no card. We confirm your business name and, for
          local businesses, your town.
        </p>
        <Screenshot
          example
          src={howItWorksDiagram.url}
          alt="Monroya setup screen confirming a business name and location before the first scan."
          label="Setup"
          caption="A quick confirmation step, then your first scan starts."
        />
      </Section>

      <Section heading="2. See who AI recommends.">
        <p>
          We ask your buyers&rsquo; questions on ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode, several times each, because
          answers change. Paid plans ask all 6; the free plan asks Gemini and Perplexity.
        </p>
        <p>
          You see who&rsquo;s named, who&rsquo;s recommended and whose pages are cited,
          stage by stage.
        </p>
        <Screenshot
          example
          src={journeyAsset.url}
          alt="Apple example: the four buyer journey stages, showing where Apple is named and where it is not."
          label="What AI says"
          caption="Apple's saved example: where buyers find you, compare you, choose, and come back."
        />
      </Section>

      <Section heading="3. Get your moves.">
        <p>
          Every week you get a ranked list, most realistic first: your own
          pages, listings, reviews, then outreach.
        </p>
        <p>Each comes with a draft built from your own facts.</p>
        <Screenshot
          example
          src={opportunitiesAsset.url}
          alt="Apple demo Jobs page listing ranked moves with drafts ready to review."
          label="Your moves"
          caption="The Jobs page from Apple's saved example, where each move comes with a ready draft."
        />
      </Section>

      <Section heading="4. Publish, and we check.">
        <p>
          Our WordPress integration sends your draft to your site with an SEO title,
          description and supported structured data. It stays unpublished until you
          review and publish it in WordPress.{" "}
          <Link to="/integrations" className="font-medium text-ink underline underline-offset-4">
            Connect WordPress
          </Link>
          .
        </p>
        <p>
          When you mark a move done, we check it&rsquo;s live and re-ask the same
          questions.
        </p>
        <p>
          You see each question move: Missing, Considered, Named, Recommended,
          Cited.
        </p>
        <Screenshot
          example
          src={briefingAsset.url}
          alt="Apple demo Mission Control showing 10 of 12 buyer questions naming Apple, stage by stage."
          label="Mission Control"
          caption="Apple's saved example: 10 of 12 buyer questions name Apple, tracked stage by stage."
        />
      </Section>

      <Section heading="5. Prove it and protect it.">
        <p>
          A win counts only when the same question on the same assistant names
          you consistently.
        </p>
        <p>
          We alert you if a win slips, and show visitors and revenue AI sends
          you when Google Analytics is connected.
        </p>
        <Screenshot
          example
          src={progressAsset.url}
          alt="Apple demo Progress page showing share of all mentions rising over repeated checks."
          label="Progress"
          caption="Apple's saved example: wins are tracked over repeated checks, not a single scan."
        />
      </Section>

      <Section heading="How we measure">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            Every question is asked several times per check; one answer never
            decides a result.
          </li>
          <li>Named, Recommended and Cited are tracked separately.</li>
          <li>
            Questions that include your brand name are scored separately, so
            your numbers measure new buyers.
          </li>
          <li>Every result links to the saved AI answer behind it.</li>
        </ul>
        <p>
          <Link
            to="/methodology"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            Full methodology &rarr;
          </Link>
        </p>
      </Section>

      <Section heading="Built for">
        <ul className="flex flex-wrap gap-3">
          <li className="border border-border bg-panel px-4 py-2 text-sm text-ink">
            Local businesses
          </li>
          <li className="border border-border bg-panel px-4 py-2 text-sm text-ink">
            Real estate agents
          </li>
          <li>
            <Link
              to="/for/b2b"
              className="inline-block border border-border bg-panel px-4 py-2 text-sm font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
            >
              Startups
            </Link>
          </li>
          <li>
            <Link
              to="/for/b2b"
              className="inline-block border border-border bg-panel px-4 py-2 text-sm font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
            >
              Marketing teams
            </Link>
          </li>
        </ul>
      </Section>
    </MarketingPage>
  );
}
